import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getEnvironment, productionUrl } from "@/lib/environment";

/**
 * Height of the fixed staging bar (matches the `h-12` class below). Published as
 * the `--staging-banner-h` CSS variable on mount; `index.css` reserves matching
 * `body` padding so content sits below the bar. The variable is ONLY ever set on
 * staging, so production stays pixel-identical.
 */
const BANNER_HEIGHT = "48px";

/**
 * Loud, non-dismissible STAGING indicator.
 *
 * Renders nothing outside staging → zero render/layout cost in production. On
 * staging it draws a fixed, centered top warning bar (animated hazard-tape edges
 * + blinking warning icons) plus a pulsing amber/red frame around the whole
 * viewport, and a one-tap "Go to live site" link that jumps to the matching
 * production URL (same path/query). Copy is localized (en/es) so non-English
 * customers and counter staff can read it. The point is to make staging
 * impossible to confuse with production.
 */
export function EnvironmentBanner() {
  const { t } = useTranslation("common", { useSuspense: false });
  const isStaging = getEnvironment() === "staging";

  useEffect(() => {
    if (!isStaging) return;
    const root = document.documentElement;
    root.style.setProperty("--staging-banner-h", BANNER_HEIGHT);
    return () => {
      root.style.removeProperty("--staging-banner-h");
    };
  }, [isStaging]);

  if (!isStaging) return null;

  return (
    <>
      {/* Top warning bar. z above any dialog overlay so it is never hidden. */}
      <div
        role="alert"
        className="fixed inset-x-0 top-0 z-[1000] h-12 overflow-hidden bg-amber-400 text-black shadow-lg"
      >
        {/* Moving hazard-tape strips along the top and bottom edges. */}
        <div aria-hidden className="staging-hazard absolute inset-x-0 top-0 h-2" />
        <div
          aria-hidden
          className="staging-hazard absolute inset-x-0 bottom-0 h-2"
        />

        {/* Equal flex spacers keep the message centered while the button sits right. */}
        <div className="relative z-10 flex h-full items-center gap-2 px-2">
          <div className="flex-1" aria-hidden />
          <span className="flex min-w-0 items-center justify-center gap-2 text-sm font-black uppercase tracking-wide sm:text-base">
            <span aria-hidden className="staging-blink shrink-0 text-lg leading-none">
              ⚠
            </span>
            <span className="truncate">
              {t("staging.warning", "Test site — bookings here are NOT real")}
            </span>
            <span aria-hidden className="staging-blink shrink-0 text-lg leading-none">
              ⚠
            </span>
          </span>
          <div className="flex flex-1 justify-end">
            <a
              href={productionUrl()}
              className="whitespace-nowrap rounded-md bg-black px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-300 shadow transition-colors hover:bg-zinc-800"
            >
              {t("staging.goLive", "Go to live site")} →
            </a>
          </div>
        </div>
      </div>

      {/* Pulsing full-viewport frame. pointer-events-none so it never intercepts
          clicks; inset-0 (not min-width) avoids the mobile layout-viewport balloon. */}
      <div
        aria-hidden
        className="staging-frame pointer-events-none fixed inset-0 z-[999] border-[6px]"
      />
    </>
  );
}

export default EnvironmentBanner;
